/**
 * Tab stops: pressing Tab advances to the next one, the way a word processor has always worked.
 *
 * A TAB IS A CHARACTER, NOT A WIDTH, and that decision is the whole design. The tempting shortcut is
 * to store the gap -- "this tab is 0.75in wide" -- which is right for exactly as long as nobody
 * edits the text before it. Type one word earlier in the line and a stored width keeps the columns
 * where they were and the text no longer lines up, which is the one thing tab stops exist to do. So
 * the document stores a `tab` node with no attributes at all, and WHERE it lands is recomputed from
 * where it sits, every time, at both ends.
 *
 * BOTH ENDS MEANS BOTH ENDS. The editor measures the rendered position; the renderer computes it
 * from font metrics. They are different machinery and they will not agree to the pixel -- but the
 * stops are ABSOLUTE positions, so as long as a run of text does not overflow past the next stop,
 * both land on the same one. That is the same guarantee a word processor gives, and it is why the
 * rule below is written once here and mirrored exactly in document_rendition.py.
 *
 * THE RULE: a tab advances to the first stop strictly greater than the current position. With no
 * stop beyond it, it advances by DEFAULT_INTERVAL -- which is what Google does with its implicit
 * half-inch grid, and what stops a tab at the end of a line from doing nothing at all.
 */
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

/** The text column, in inches. Matches DocumentPaper and the ruler; if one changes all three must. */
export const MEASURE_INCHES = 6.5;

/** Where a tab goes when no explicit stop lies beyond it. Google's implicit grid. */
export const DEFAULT_INTERVAL = 0.5;

/** The shared rule. Given a position and the paragraph's stops, where does a tab land? */
export function nextStop(fromInches: number, stops: number[]): number {
    const beyond = stops.filter((s) => s > fromInches + 0.001).sort((a, b) => a - b);
    if (beyond.length) return beyond[0];
    const grid = Math.floor(fromInches / DEFAULT_INTERVAL + 0.001) + 1;
    return Math.min(grid * DEFAULT_INTERVAL, MEASURE_INCHES);
}

/**
 * The tab itself: an inline atom carrying nothing.
 *
 * NOT A TEXT CHARACTER. A literal "\t" in a ProseMirror text node is collapsed by HTML whitespace
 * handling and would have to be fought with white-space:pre, which changes how every other space in
 * the document behaves. A node is also what lets the renderer find them without parsing text.
 */
export const Tab = Node.create({
    name: "tab",
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,

    parseHTML() {
        return [{ tag: "span[data-tab]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["span", mergeAttributes(HTMLAttributes, { "data-tab": "", class: "parkour-tab" })];
    },

    addKeyboardShortcuts() {
        return {
            // INSIDE A LIST, TAB STILL NESTS, because that is what Tab means there and a procedure's
            // numbered steps are lists. Only outside one does it advance to a stop.
            Tab: ({ editor }) => {
                if (editor.isActive("listItem") || editor.isActive("taskItem")) return false;
                if (editor.isActive("table")) return false;
                return editor.commands.insertContent({ type: "tab" });
            },
        };
    },
});

const key = new PluginKey("tabWidths");

/**
 * Give every rendered tab its width.
 *
 * DONE IN ONE PASS OVER THE DOM rather than with a node view each, because a tab's width depends on
 * the text BEFORE it: typing a word at the start of a line moves every tab after it, and a node view
 * only knows when its own node changed. One pass after each update is both simpler and correct.
 *
 * MEASURED IN INCHES OFF THE TEXT COLUMN, never in pixels: the editor zooms, and a width computed in
 * pixels at one zoom is wrong at every other.
 */
export const TabWidths = Extension.create({
    name: "tabWidths",

    addProseMirrorPlugins() {
        const size = (root: HTMLElement) => {
            const content = root.getBoundingClientRect();
            if (!content.width) return;
            const pxPerInch = content.width / MEASURE_INCHES;

            root.querySelectorAll<HTMLElement>("span[data-tab]").forEach((tab) => {
                // THE BLOCK IT SITS IN carries the stops and the indent, and the indent matters:
                // positions are measured from where the paragraph's text starts, not from the page.
                const block = tab.closest("p, h1, h2, h3, h4, h5, h6, blockquote") as HTMLElement | null;
                const raw = block?.getAttribute("data-tab-stops");
                const stops = raw ? raw.split(",").map(parseFloat).filter(Number.isFinite) : [];
                const blockLeft = block ? block.getBoundingClientRect().left : content.left;

                // Width zero first, so the measurement is of the text BEFORE this tab rather than of
                // the text plus whatever this tab was last time.
                tab.style.width = "0px";
                const at = (tab.getBoundingClientRect().left - blockLeft) / pxPerInch;
                const target = nextStop(at, stops);
                tab.style.width = `${Math.max(0, (target - at) * pxPerInch)}px`;
            });
        };

        return [new Plugin({
            key,
            view: (view) => {
                const run = () => size(view.dom as HTMLElement);
                // AFTER THE BROWSER HAS LAID OUT, not during the update: measuring inside the
                // transaction reads the previous frame's geometry and every tab is one edit stale.
                const schedule = () => requestAnimationFrame(run);
                schedule();
                const observer = new ResizeObserver(schedule);
                observer.observe(view.dom as HTMLElement);
                return {
                    update: schedule,
                    destroy: () => observer.disconnect(),
                };
            },
        })];
    },
});
